<template>
  <div class="auth-page">
    <div class="auth-bg"></div>
    <div class="auth-grid"></div>

    <div class="auth-container">
      <div class="auth-card">
        <!-- Header -->
        <div class="auth-header">
          <div class="auth-logo">
            <div class="auth-logo-icon">CS</div>
            <span class="auth-logo-text">Casino<span class="accent">Script</span></span>
          </div>
          <h1 class="auth-title">{{ $t('auth.login_title') }}</h1>
          <p class="auth-subtitle">{{ $t('auth.login_subtitle') }}</p>
        </div>

        <!-- Error Alert -->
        <div v-if="errorMessage" class="auth-error">
          <span class="auth-error-icon">!</span>
          <span>{{ errorMessage }}</span>
        </div>

        <!-- Login Form -->
        <form class="auth-form" @submit.prevent="handleSubmit">
          <template v-if="!showTwoFactor">
            <!-- Email -->
            <div class="form-group">
              <label class="form-label" for="email">{{ $t('auth.email_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
                <input
                  id="email"
                  v-model="email"
                  type="email"
                  class="form-input"
                  :placeholder="$t('auth.email_placeholder')"
                  required
                  autocomplete="email"
                />
              </div>
            </div>

            <!-- Password -->
            <div class="form-group">
              <label class="form-label" for="password">{{ $t('auth.password_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                </svg>
                <input
                  id="password"
                  v-model="password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input"
                  :placeholder="$t('auth.password_placeholder')"
                  required
                  autocomplete="current-password"
                />
                <button
                  type="button"
                  class="form-input-toggle"
                  @click="showPassword = !showPassword"
                  tabindex="-1"
                >
                  <svg v-if="!showPassword" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                  </svg>
                  <svg v-else viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                    <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Remember & Forgot -->
            <div class="form-remember">
              <label class="remember-label">
                <input v-model="rememberMe" type="checkbox" class="remember-checkbox" />
                <span class="remember-check-custom" :class="{ checked: rememberMe }">
                  <svg v-if="rememberMe" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M10.28 2.28a.75.75 0 00-1.06-1.06L4.5 5.94 2.78 4.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l5.25-5.25z" />
                  </svg>
                </span>
                {{ $t('auth.remember_me') }}
              </label>
              <router-link to="/forgot-password" class="forgot-link">{{ $t('auth.forgot_password_link') }}</router-link>
            </div>
          </template>

          <!-- 2FA Code Input -->
          <template v-else>
            <div class="twofa-section">
              <div class="twofa-icon-wrap">
                <svg viewBox="0 0 24 24" fill="currentColor" class="twofa-icon">
                  <path d="M12 1C8.676 1 6 3.676 6 7v2H4a2 2 0 00-2 2v10a2 2 0 002 2h16a2 2 0 002-2V11a2 2 0 00-2-2h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v2H8V7c0-2.276 1.724-4 4-4zm0 10a2 2 0 011 3.732V19a1 1 0 01-2 0v-2.268A2 2 0 0112 13z" />
                </svg>
              </div>
              <h2 class="twofa-title">{{ $t('auth.two_factor_title') }}</h2>
              <p class="twofa-subtitle">{{ $t('auth.two_factor_subtitle') }}</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="twoFactorCode">{{ $t('auth.two_factor_code_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                </svg>
                <input
                  id="twoFactorCode"
                  ref="twoFactorInput"
                  v-model="twoFactorCode"
                  type="text"
                  class="form-input"
                  placeholder="000000"
                  maxlength="8"
                  autocomplete="one-time-code"
                  inputmode="numeric"
                />
              </div>
              <p class="form-hint">{{ $t('auth.two_factor_hint') }}</p>
            </div>

            <button type="button" class="twofa-back" @click="cancelTwoFactor">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
              </svg>
              {{ $t('auth.back_to_login') }}
            </button>
          </template>

          <!-- Sign In Button -->
          <button type="submit" class="btn-signin" :disabled="isLoading">
            <span v-if="isLoading" class="btn-spinner"></span>
            <span v-else>{{ showTwoFactor ? $t('auth.verify') : $t('auth.sign_in') }}</span>
          </button>
        </form>

        <!-- Footer -->
        <div class="auth-footer">
          {{ $t('auth.no_account') }} <router-link to="/register">{{ $t('auth.create_one_now') }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const userStore = useUserStore()

const email = ref('')
const password = ref('')
const rememberMe = ref(false)
const showPassword = ref(false)
const twoFactorCode = ref('')
const showTwoFactor = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const twoFactorInput = ref<HTMLInputElement | null>(null)

function cancelTwoFactor() {
  showTwoFactor.value = false
  twoFactorCode.value = ''
  errorMessage.value = ''
}

async function handleSubmit() {
  errorMessage.value = ''
  isLoading.value = true

  try {
    const code = showTwoFactor.value ? twoFactorCode.value.trim() : undefined
    const result = await userStore.login(email.value, password.value, code)

    if (result.requires_2fa) {
      showTwoFactor.value = true
      await nextTick()
      twoFactorInput.value?.focus()
      return
    }

    const redirect = (route.query.redirect as string) || '/player/lobby'
    router.push(redirect)
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string }; status?: number } }
    const status = axiosError.response?.status
    const message = axiosError.response?.data?.message

    if (status === 423) {
      errorMessage.value = message || t('auth.login_error_locked')
    } else if (status === 403) {
      errorMessage.value = message || t('auth.login_error_suspended')
    } else if (status === 422) {
      errorMessage.value = message || t('auth.login_error_input')
    } else if (status === 401) {
      errorMessage.value = showTwoFactor.value
        ? t('auth.login_error_invalid_2fa')
        : t('auth.login_error_invalid_creds')
    } else {
      errorMessage.value = message || t('auth.error_generic')
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  overflow: hidden;
  position: relative;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background-image:
    radial-gradient(ellipse at 20% 50%, rgba(0, 231, 1, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(20, 117, 225, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.auth-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.auth-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  padding: var(--space-5);
}

.auth-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.95) 0%, rgba(33, 55, 67, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl);
  padding: var(--space-10);
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.6s ease both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.auth-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.auth-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.auth-logo-icon {
  width: 40px;
  height: 40px;
  background: var(--accent-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-family-display);
  box-shadow: var(--shadow-glow-green);
}

.auth-logo-text {
  font-family: var(--font-family-display);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.auth-logo-text .accent {
  color: var(--accent-primary);
}

.auth-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.auth-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
}

/* Error Alert */
.auth-error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-light);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-4);
}

.auth-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
}

.form-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.form-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: var(--text-tertiary);
  pointer-events: none;
}

.form-input {
  width: 100%;
  background: rgba(15, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  padding: 12px 16px;
  padding-left: 40px;
  color: var(--text-primary);
  font-size: var(--font-size-body);
  font-family: var(--font-family-primary);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
  background: rgba(15, 25, 35, 1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

.form-input-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-tertiary);
  transition: color var(--transition-fast);
  display: flex;
  align-items: center;
}

.form-input-toggle:hover {
  color: var(--text-secondary);
}

.form-input-toggle svg {
  width: 18px;
  height: 18px;
}

.form-hint {
  font-size: var(--font-size-sm);
  color: var(--text-tertiary);
}

/* Remember Me */
.form-remember {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
}

.remember-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  cursor: pointer;
}

.remember-checkbox {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.remember-check-custom {
  width: 18px;
  height: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-sm, 4px);
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  flex-shrink: 0;
}

.remember-check-custom:hover {
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.08);
}

.remember-check-custom.checked {
  background: var(--accent-primary);
  border-color: var(--accent-primary);
}

.remember-check-custom svg {
  width: 12px;
  height: 12px;
  color: #000;
}

.forgot-link {
  color: var(--accent-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.forgot-link:hover {
  color: var(--text-primary);
  text-decoration: underline;
}

/* 2FA Section */
.twofa-section {
  text-align: center;
  margin-bottom: var(--space-4);
}

.twofa-icon-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: var(--space-3);
}

.twofa-icon {
  width: 48px;
  height: 48px;
  color: var(--accent-secondary);
}

.twofa-title {
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.twofa-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
}

.twofa-back {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: none;
  border: none;
  color: var(--text-tertiary);
  font-size: var(--font-size-body);
  cursor: pointer;
  padding: 0;
  transition: color var(--transition-fast);
}

.twofa-back:hover {
  color: var(--text-primary);
}

.twofa-back svg {
  width: 16px;
  height: 16px;
}

/* Sign In Button */
.btn-signin {
  width: 100%;
  padding: 12px 16px;
  background: var(--accent-primary);
  color: var(--color-on-accent);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-primary);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
}

.btn-signin:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: var(--shadow-glow-green);
  transform: translateY(-2px);
}

.btn-signin:active:not(:disabled) {
  transform: translateY(0);
}

.btn-signin:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: var(--radius-full);
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Footer */
.auth-footer {
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
}

.auth-footer a {
  color: var(--accent-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.auth-footer a:hover {
  color: var(--text-primary);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 28px;
  }

  .auth-title {
    font-size: 20px;
  }
}
</style>
